import axios from "axios";

// Obter histórico de chamadas
export const getCallHistory = async (page = 1, limit = 20, token) => {
  try {
    const { data } = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/calls?page=${page}&limit=${limit}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return { status: "ok", ...data };
  } catch (error) {
    return {
      status: "error",
      message: error.response?.data?.message || "Erro ao buscar histórico",
    };
  }
};

// Obter detalhes de uma chamada
export const getCall = async (callId, token) => {
  try {
    const { data } = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/calls/${callId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return { status: "ok", data };
  } catch (error) {
    return {
      status: "error",
      message: error.response?.data?.message || "Erro ao buscar chamada",
    };
  }
};

// Deletar chamada do histórico
export const deleteCall = async (callId, token) => {
  try {
    const { data } = await axios.delete(
      `${process.env.REACT_APP_BACKEND_URL}/calls/${callId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return { status: "ok", data };
  } catch (error) {
    return {
      status: "error",
      message: error.response?.data?.message || "Erro ao deletar chamada",
    };
  }
};

// Formatar duração da chamada
export const formatCallDuration = (seconds) => {
  if (!seconds || seconds === 0) return "0:00";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  }
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
};

// Formatar status da chamada
export const formatCallStatus = (status, type, isCaller) => {
  const statusMap = {
    ringing: "Chamando...",
    ongoing: "Em andamento",
    ended: "Finalizada",
    missed: isCaller ? "Não atendida" : "Chamada perdida",
    rejected: isCaller ? "Recusada" : "Você recusou",
  };
  return statusMap[status] || status;
};

// Obter ícone do tipo de chamada
export const getCallTypeIcon = (type) => {
  return type === "video" ? "fa-video-camera" : "fa-phone";
};

// ========================================
// CONFERENCE API FUNCTIONS
// ========================================

// Criar conferência
export const createConference = async (title, type = "video", settings = {}, token) => {
  try {
    const { data } = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/conference`,
      { title, type, settings },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return { status: "ok", conference: data.conference };
  } catch (error) {
    return {
      status: "error",
      message: error.response?.data?.message || "Erro ao criar conferência",
    };
  }
};

// Buscar conferência por código
export const getConferenceByCode = async (code, token) => {
  try {
    const { data } = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/conference/${code}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return { status: "ok", conference: data };
  } catch (error) {
    return {
      status: "error",
      message: error.response?.data?.message || "Conferência não encontrada",
    };
  }
};

// Buscar minhas conferências ativas
export const getMyConferences = async (token) => {
  try {
    const { data } = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/conference/my`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return { status: "ok", conferences: data };
  } catch (error) {
    return {
      status: "error",
      message: error.response?.data?.message || "Erro ao buscar conferências",
    };
  }
};

// Entrar em uma conferência
export const joinConference = async (code, token) => {
  try {
    const { data } = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/conference/${code}/join`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return {
      status: "ok",
      conference: data.conference,
      inWaitingRoom: data.inWaitingRoom || false,
    };
  } catch (error) {
    return {
      status: "error",
      message: error.response?.data?.message || "Erro ao entrar na conferência",
    };
  }
};

// Sair de uma conferência
export const leaveConference = async (conferenceId, token) => {
  try {
    await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/conference/${conferenceId}/leave`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return { status: "ok" };
  } catch (error) {
    return {
      status: "error",
      message: error.response?.data?.message || "Erro ao sair da conferência",
    };
  }
};

// Encerrar conferência (apenas host)
export const endConference = async (conferenceId, token) => {
  try {
    await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/conference/${conferenceId}/end`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return { status: "ok" };
  } catch (error) {
    return {
      status: "error",
      message: error.response?.data?.message || "Erro ao encerrar conferência",
    };
  }
};

// Admitir usuário da sala de espera (apenas host)
export const admitFromWaitingRoom = async (conferenceId, visitorId, token) => {
  try {
    await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/conference/${conferenceId}/admit`,
      { visitorId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return { status: "ok" };
  } catch (error) {
    return {
      status: "error",
      message: error.response?.data?.message || "Erro ao admitir participante",
    };
  }
};

// Recusar usuário da sala de espera (apenas host)
export const denyFromWaitingRoom = async (conferenceId, visitorId, token) => {
  try {
    await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/conference/${conferenceId}/deny`,
      { visitorId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return { status: "ok" };
  } catch (error) {
    return {
      status: "error",
      message: error.response?.data?.message || "Erro ao recusar participante",
    };
  }
};

// Remover participante (apenas host)
export const removeParticipant = async (conferenceId, participantId, token) => {
  try {
    await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/conference/${conferenceId}/remove`,
      { participantId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return { status: "ok" };
  } catch (error) {
    return {
      status: "error",
      message: error.response?.data?.message || "Erro ao remover participante",
    };
  }
};

// Atualizar status de mídia do participante
export const updateParticipantMedia = async (conferenceId, mediaStatus, token) => {
  try {
    await axios.put(
      `${process.env.REACT_APP_BACKEND_URL}/conference/${conferenceId}/media`,
      mediaStatus,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return { status: "ok" };
  } catch (error) {
    return {
      status: "error",
      message: error.response?.data?.message || "Erro ao atualizar mídia",
    };
  }
};

// Atualizar configurações da conferência (apenas host)
export const updateConferenceSettings = async (conferenceId, settingsData, token) => {
  try {
    await axios.put(
      `${process.env.REACT_APP_BACKEND_URL}/conference/${conferenceId}/settings`,
      settingsData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return { status: "ok" };
  } catch (error) {
    return {
      status: "error",
      message: error.response?.data?.message || "Erro ao atualizar configurações",
    };
  }
};

// Convidar usuários para conferência
export const inviteToConference = async (conferenceId, userIds, token) => {
  try {
    await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/conference/${conferenceId}/invite`,
      { userIds },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return { status: "ok" };
  } catch (error) {
    return {
      status: "error",
      message: error.response?.data?.message || "Erro ao enviar convites",
    };
  }
};
